import { buildHeaders } from "./apiConfig";
import { DOWNLOAD_API_URL } from "../constants";

const httpErrorMessage = (status) => {
  switch (status) {
    case 400:
      return "Invalid request — filename parameter is missing. Please try again.";
    case 403:
      return "Permission denied — the server does not have access to the storage bucket. Contact support.";
    case 404:
      return "No output files found. The job may still be processing or the filename is incorrect.";
    case 500:
      return "Internal server error. Please wait a moment and try again.";
    default:
      return `Unexpected error (HTTP ${status}). Please try again.`;
  }
};

export async function fetchDownloadFiles(filename, jobId) {
  if (!filename) throw new Error("No filename provided for download.");
  if (!jobId) throw new Error("No jobId provided for download.");

  if (!DOWNLOAD_API_URL) {
    throw new Error(
      "Download service is not configured. " +
        "REACT_APP_DOWNLOAD_API_URL is missing — contact support or check your environment settings."
    );
  }

  const url = DOWNLOAD_API_URL.replace(/\/$/, "");
  const payload = JSON.stringify({ job_id: jobId, filename: filename });

  // eslint-disable-next-line no-console
  console.log(`[S3DownloadService] Fetch download files — jobId: ${jobId}, filename: ${filename}`);

  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: buildHeaders(),
      body: payload,
    });
  } catch (networkErr) {
    throw new Error("Network error while checking sync status. Please check your connection.");
  }

  if (!response.ok) {
    let message = httpErrorMessage(response.status);
    try {
      const errData = await response.json();
      if (errData?.error) message = errData.error;
    } catch {
      /* use status-derived message */
    }
    // eslint-disable-next-line no-console
    console.error(`[S3 Download] ❌ ${response.status} — ${message}`);
    throw new Error(message);
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error("Invalid response from server. Please try again.");
  }

  const files = Array.isArray(data?.files) ? data.files : [];
  const srtFiles = Array.isArray(data?.srt_files) ? data.srt_files : [];

  const allFiles = [...files, ...srtFiles];

  if (allFiles.length === 0) {
    // eslint-disable-next-line no-console
    console.error("[S3 Download] ❌ Response missing both 'files' and 'srt_files' arrays:", data);
    throw new Error("No files found in server response. Please try again.");
  }

  // eslint-disable-next-line no-console
  console.log(
    `[S3 Download] ✅ ${files.length} video file(s) + ${srtFiles.length} SRT file(s) ready for download.`
  );
  return allFiles; // [{ filename, url }, ...]
}

// ─────────────────────────────────────────────────────────────────────────────
// File System Access API support (Chromium only).
// When a directory handle is supplied, files are streamed straight to disk and
// the byte count is verified against Content-Length. writable.close() resolves
// only after the data is committed, so "done" means "actually saved".
// If unsupported / not supplied, the legacy blob/anchor path is used unchanged.
// To revert: stop passing dirHandle (or delete this block + the dirHandle branches).
// ─────────────────────────────────────────────────────────────────────────────
export const isFileSystemAccessSupported = () =>
  typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";

async function writeFileToDirectory(dirHandle, filename, url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Download failed: ${response.status}`);

  const expected = Number(response.headers.get("content-length") || 0);
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();

  let received = 0;
  try {
    const reader = response.body.getReader();
    let result;
    while (!(result = await reader.read()).done) {
      received += result.value.byteLength;
      await writable.write(result.value);
    }
  } catch (err) {
    // Discard the partial file instead of committing it.
    try {
      await writable.abort();
    } catch {
      /* ignore */
    }
    throw err;
  }

  // Verify BEFORE committing so an incomplete file is never saved as complete.
  if (expected && received !== expected) {
    try {
      await writable.abort();
    } catch {
      /* ignore */
    }
    throw new Error(`Incomplete file ${filename}: received ${received} of ${expected} bytes.`);
  }

  await writable.close(); // resolves only once written to disk
}

export async function downloadAllFilesParallel(files, onFileProgress, dirHandle = null) {
  const results = await Promise.allSettled(
    files.map(async ({ filename, url }) => {
      if (!url) throw new Error(`Missing URL for file: ${filename}`);

      onFileProgress?.(filename, "downloading");
      // eslint-disable-next-line no-console
      console.log(`[S3 Download] ⬇️  Starting: ${filename}`);

      try {
        if (dirHandle) {
          await writeFileToDirectory(dirHandle, filename, url);
        } else {
          await triggerBrowserDownload(url, filename);
        }
        onFileProgress?.(filename, "done");
        // eslint-disable-next-line no-console
        console.log(`[S3 Download] ✅ Done: ${filename}`);
      } catch (err) {
        onFileProgress?.(filename, "error", err.message);
        // eslint-disable-next-line no-console
        console.error(`[S3 Download] ❌ Failed: ${filename} — ${err.message}`);
        throw err;
      }
    })
  );

  const succeeded = results.filter((r) => r.status === "fulfilled").length;
  const failed = results.filter((r) => r.status === "rejected").length;
  const errors = results
    .filter((r) => r.status === "rejected")
    .map((r) => r.reason?.message || "Unknown error");

  // eslint-disable-next-line no-console
  console.log(`[S3 Download] 🏁 Complete — ${succeeded} succeeded, ${failed} failed.`);
  return { succeeded, failed, errors };
}

export async function downloadSingleFile(
  filename,
  videoFileName,
  jobId,
  onFileProgress,
  dirHandle = null
) {
  try {
    // Re-fetch fresh pre-signed URLs (originals may have expired)
    const files = await fetchDownloadFiles(videoFileName, jobId);

    // Find the specific file in the refreshed list
    const target = files.find((f) => f.filename === filename);
    if (!target) throw new Error(`File "${filename}" not found in server response.`);

    // Trigger download for just this one file
    onFileProgress?.(filename, "downloading");
    // eslint-disable-next-line no-console
    console.log(`[S3 Download] 🔁 Retrying: ${filename}`);

    if (dirHandle) {
      await writeFileToDirectory(dirHandle, filename, target.url);
    } else {
      await triggerBrowserDownload(target.url, filename);
    }
    onFileProgress?.(filename, "done");
    // eslint-disable-next-line no-console
    console.log(`[S3 Download] ✅ Retry succeeded: ${filename}`);

    return { succeeded: 1, failed: 0 };
  } catch (err) {
    onFileProgress?.(filename, "error", err.message);
    // eslint-disable-next-line no-console
    console.error(`[S3 Download] ❌ Retry failed: ${filename} — ${err.message}`);
    return { succeeded: 0, failed: 1 };
  }
}

async function triggerBrowserDownload(url, filename) {
  const anchorClick = (href) => {
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = filename || "download";
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => document.body.removeChild(anchor), 200);
  };

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Blob fetch failed: ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    anchorClick(objectUrl);

    setTimeout(() => URL.revokeObjectURL(objectUrl), 500);
    // eslint-disable-next-line no-console
    console.log(`[S3 Download] Blob download triggered for: ${filename}`);
  } catch (err) {
    console.warn(`[S3 Download] Blob fetch blocked (${err.message}). Using direct URL fallback.`);

    try {
      const directUrl = new URL(url);
      directUrl.searchParams.set(
        "response-content-disposition",
        `attachment; filename="${filename || "download"}"`
      );
      anchorClick(directUrl.toString());
      // eslint-disable-next-line no-console
      console.log(`[S3 Download] Direct URL download triggered for: ${filename}`);
    } catch {
      // Last resort: open in new tab
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }
}
